import React, { useState, useEffect, useRef } from "react";
import { Card, CardBody, Col, Container, Row } from "reactstrap";
import { useTranslation } from "react-i18next";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const LineChart: React.FC = () => {
  const { t } = useTranslation();
  const [timePeriod, setTimePeriod] = useState<"day" | "week" | "month">("day");
  const cardBodyRef = useRef<HTMLDivElement>(null);
  const [isScrolling, setIsScrolling] = useState(false);

  const timePeriodOrder = ["day", "week", "month"];

  useEffect(() => {
    const handleWheel = (event: WheelEvent) => {
      if (isScrolling) return;

      setIsScrolling(true);
      const currentIndex = timePeriodOrder.indexOf(timePeriod);

      if (event.deltaY < 0) {
        if (currentIndex < timePeriodOrder.length - 1) {
          setTimePeriod(
            timePeriodOrder[currentIndex + 1] as "day" | "week" | "month"
          );
        }
      } else if (event.deltaY > 0) {
        if (currentIndex > 0) {
          setTimePeriod(
            timePeriodOrder[currentIndex - 1] as "day" | "week" | "month"
          );
        }
      }

      setTimeout(() => setIsScrolling(false), 200);
    };

    const cardBody = cardBodyRef.current;
    if (cardBody) {
      cardBody.addEventListener("wheel", handleWheel);
    }

    return () => {
      if (cardBody) {
        cardBody.removeEventListener("wheel", handleWheel);
      }
    };
  }, [timePeriod, isScrolling]);

  const chartData = {
    day: {
      labels: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
      liveActive: [50, 60, 55, 70, 65, 80, 75],
      liveInactive: [10, 15, 12, 20, 18, 25, 22],
      trialActive: [30, 35, 40, 45, 50, 55, 60],
      trialInactive: [5, 8, 7, 10, 12, 15, 14],
    },
    week: {
      labels: ["Week 1", "Week 2", "Week 3", "Week 4"],
      liveActive: [200, 220, 210, 230],
      liveInactive: [40, 50, 45, 55],
      trialActive: [120, 130, 140, 150],
      trialInactive: [20, 25, 22, 30],
    },
    month: {
      labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
      liveActive: [1000, 1100, 1050, 1200, 1150, 1250],
      liveInactive: [200, 220, 210, 230, 240, 250],
      trialActive: [600, 650, 700, 750, 800, 850],
      trialInactive: [100, 110, 120, 130, 140, 150],
    },
  };

  const current = chartData[timePeriod];

  const chartJsData = {
    labels: current.labels,
    datasets: [
      {
        label: t("Live Active"),
        data: current.liveActive,
        borderColor: "#4CAF50",
        backgroundColor: "rgba(76, 175, 80, 0.2)",
        fill: true,
        tension: 0.4,
      },
      {
        label: t("Live Inactive"),
        data: current.liveInactive,
        borderColor: "#F44336",
        backgroundColor: "rgba(244, 67, 54, 0.2)",
        fill: true,
        tension: 0.4,
      },
      {
        label: t("Trial Active"),
        data: current.trialActive,
        borderColor: "#2196F3",
        backgroundColor: "rgba(33, 150, 243, 0.2)",
        fill: true,
        tension: 0.4,
      },
      {
        label: t("Trial Inactive"),
        data: current.trialInactive,
        borderColor: "#FF9800",
        backgroundColor: "rgba(255, 152, 0, 0.2)",
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom" as const,
      },
      tooltip: {
        mode: "index" as const,
        intersect: false,
      },
    },
    scales: {
      x: {
        title: { display: true, text: t("Time") },
      },
      y: {
        title: { display: true, text: t("Count") },
        beginAtZero: true,
      },
    },
    interaction: {
      mode: "nearest" as const,
      axis: "x" as const,
      intersect: false,
    },
  };

  return (
    <Container fluid>
      <Row>
        <Col sm="12">
          <Card>
            {/* <CommonCardHeader
              headClass="pb-4"
              title=""
              mainTitle={true}
              firstItem="Weekly"
              secondItem="Monthly"
              thirdItem="Yearly"
            /> */}
            <CardBody innerRef={cardBodyRef}>
              <div style={{ position: "relative", height: "500px" }}>
                <Line data={chartJsData} options={chartOptions} />
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default LineChart;
